import {
  Box,
  Button,
  Grid,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { GoDotFill } from "react-icons/go";
import { Link, useNavigate } from "react-router";
import dayjs from "../../helpers/dayJs";
import { IoMailOutline, IoWarning } from "react-icons/io5";
import useMessages from "../../hooks/data/useMessages";
import { useState } from "react";
import { HiCheck, HiChevronDown } from "react-icons/hi2";
import { TbExternalLink } from "react-icons/tb";

function MessagesUI() {
  const navigate = useNavigate();
  const { messages, isLoading, markAsRead, markAllAsRead, getUnreadCount } =
    useMessages();

  const [currentMessageId, setCurrentMessageId] = useState();

  function handleItemClick(n) {
    if (!n.isRead) markAsRead(n.id);
    if (n.customPath) navigate(n.customPath);
    setCurrentMessageId(n.id);
  }

  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={Button}
        aria-label="Notifications"
        color="gray.400"
        bg="transparent"
        borderRadius={"full"}
        h="45px"
        w="45px"
        variant={"nav"}
        size="lg"
        minW="0"
        p="0.5"
        display="flex"
        alignItems="center"
        justifyContent="center"
        pos="relative"
        leftIcon={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -52%)"
            w="100%"
            h="100%"
            pointerEvents="none"
          >
            <IoMailOutline size={19} />
          </Box>
        }
        rightIcon={
          getUnreadCount() > 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-30%, -35%)"
              w="100%"
              h="100%"
              pointerEvents="none"
              color="red.400"
            >
              <GoDotFill />
            </Box>
          )
        }
      ></MenuButton>
      <MenuList color={"white"} p="1" w="350px">
        <Text fontSize={"md"} fontWeight={"semibold"} px="3" py="1.5">
          Messages
        </Text>
        {isLoading && messages.length === 0 && (
          <MenuItem>
            <Text fontSize="sm" color="gray.300">
              Loading messages...
            </Text>
          </MenuItem>
        )}

        {!isLoading && messages.length === 0 && (
          <MenuItem>
            <Text fontSize="sm" color="gray.300">
              No messages yet
            </Text>
          </MenuItem>
        )}

        {messages.map((n, index) => (
          <MenuItem
            key={n.id}
            h={"max-content"}
            onClick={() => handleItemClick(n)}
            borderBottom={index < messages?.length - 1 ? "1px solid" : "none"}
            borderBottomColor={"gray.700"}
          >
            <Grid
              as={Grid}
              gridTemplateRows={"repeat(2, auto)"}
              // gridTemplateColumns={"20px 1fr"}
              columnGap={"3"}
              py="1"
              rowGap={"1"}
              w="full"
            >
              <HStack spacing={"1"} w="full">
                {!n.isRead && (
                  <Box color="red.400">
                    <GoDotFill />
                  </Box>
                )}
                <Text
                  fontWeight={"semibold"}
                  color={n.type === "business" ? "green.400" : "red.400"}
                >
                  {n.type === "business"
                    ? "Business Inquiry"
                    : n.type === "fanmail"
                      ? "❤️ Fan Mail"
                      : "Message"}
                </Text>

                <Text fontSize={"xs"} color={"gray.400"}>
                  {" - "}
                  {dayjs(n?.createdAt?.toDate()).calendar()}
                </Text>
              </HStack>
              <VStack align={"flex-start"} spacing={"0.5"} pt="1">
                <Text noOfLines={2}>{n.message}</Text>
                <Text fontSize={"xs"} color="gray.400">
                  Click to view
                </Text>
              </VStack>
            </Grid>
          </MenuItem>
        ))}
      </MenuList>

      <MessageModal
        isOpen={!!currentMessageId}
        onClose={() => setCurrentMessageId(null)}
        messageId={currentMessageId}
      />
    </Menu>
  );
}

const MessageModal = ({ messageId, isOpen, onClose }) => {
  const { updateMessage, messages } = useMessages();
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    id,
    createdAt,
    fanUserId,
    fanUserEmail,
    fanUserUsername,
    fanUserFullname,
    isRead,
    status,
    message,
    type,
  } = messages.find(({ id }) => id === messageId) || {};
  /**
   *
   * @param {'safe' | 'spam'} type
   */
  async function markAs(type) {
    setIsUpdating(true);
    await updateMessage({
      messageId: id,
      data: {
        status: type,
      },
    });
    setIsUpdating(false);
  }

  return (
    <Modal size="md" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {type === "business" ? "Business Inquiry" : "Fanmail"}
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody px="2" color="white">
          <VStack w="full" align={"flex-start"} pb="3">
            <HStack w="full" spacing={"1"}>
              <Text fontWeight={"semibold"} fontSize={"md"}>
                Message
              </Text>
              <Text fontSize={"sm"} color={"gray.400"}>
                on {dayjs(createdAt?.toDate()).calendar()}
              </Text>
            </HStack>
            <Text fontSize={"sm"}>{message}</Text>
            <Text fontWeight={"semibold"} fontSize={"md"} pt="2">
              From
            </Text>
            <HStack
              bg="#2a2a2c"
              px="2"
              py="1"
              borderRadius={"md"}
              fontWeight={"semibold"}
              border="1px dashed"
              borderColor={"gray.700"}
              w="full"
              justify={"space-between"}
            >
              <Text
                fontSize={"sm"}
                filter={!fanUserEmail && !fanUserFullname && "blur(5px)"}
              >
                {fanUserEmail || fanUserFullname || "hidden@hidden.com"}
              </Text>
              {status === "safe" && !fanUserEmail && (
                <Spinner size="xs" thickness="1px" />
              )}
              {((status === "safe" && fanUserEmail) || type === "fanmail") && (
                <Button
                  as={Link}
                  color="white"
                  variant={"nav"}
                  size="xs"
                  target="_blank"
                  to={`/user/${fanUserUsername}`}
                  rightIcon={<TbExternalLink />}
                >
                  View Profile
                </Button>
              )}
            </HStack>
            {status === "unknown" && type === "business" && (
              <Text fontSize={"xs"} color="gray.400">
                In order to see the sender's details, you must mark this message
                as "safe". Otherwise, mark this message as a spam.
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant={"nav"}>
            Close
          </Button>
          <Menu placement="bottom">
            <MenuButton as={Button} size="sm" rightIcon={<HiChevronDown />}>
              Mark as
            </MenuButton>
            <MenuList color="white">
              <MenuItem onClick={() => markAs("safe")}>
                <HiCheck /> Mark as safe
              </MenuItem>
              <MenuItem color="red.400" onClick={() => markAs("spam")}>
                <IoWarning />
                Mark as spam
              </MenuItem>
            </MenuList>
          </Menu>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MessagesUI;
